<template>
  <div>
    <v-btn
      variant="tonal"
      block
      :loading="loading"
      :disabled="disabled"
      @click="pickFile"
    >
      <v-icon start>folder_open</v-icon>Load backup code file
    </v-btn>
    <!-- The saved name ends in the date the code was minted (2026-10-10),
         so the caption names what it STARTS with — and which of several to
         pick: a new code replaces the old, so only the newest file works. -->
    <p class="text-caption text-medium-emphasis text-center mt-1 mb-0">
      Look for a file whose name starts with <strong>{{ expectedFilePrefix }}</strong>, usually in your
      Downloads folder. If there are several, use the newest one.
    </p>
    <!-- The file names an account other than the one being unlocked
         (2026-10-10). A code only opens the identity it was minted for, so
         say so before trying — but the check reads a line of text, not the
         code, so the user keeps the last word. -->
    <v-alert v-if="mismatch" type="warning" variant="tonal" density="compact" class="mt-3">
      This file holds the backup code for <strong>{{ mismatch.fileEmail }}</strong>, not
      <strong>{{ myEmail }}</strong>. A backup code only unlocks the account it was created for,
      so it probably won't work here.
      <div class="d-flex flex-wrap justify-end mt-2" style="gap: 8px;">
        <v-btn variant="text" size="small" :disabled="disabled" @click="pickFile">Choose a different file</v-btn>
        <v-btn variant="outlined" size="small" :disabled="disabled" @click="useAnyway">Try it anyway</v-btn>
      </div>
    </v-alert>
    <input
      ref="fileInput"
      type="file"
      accept=".txt,text/plain"
      style="display: none"
      @change="onFileInputChange"
    />
  </div>
</template>

<script>
  // Primary NC1 recovery-code unlock: picks the .txt that
  // RecoveryCodeDialog's Download button saved (or any text file holding the
  // code) and emits the dash-grouped code; emits `error` when none is found
  // (and `error` with null once a new file was read, so the parent drops its
  // last message). When the file's `Account:` line names someone other than
  // `email`, nothing is emitted until the user says "Try it anyway".
  // Browsers can only filter pickers by extension, so the file stays .txt
  // (it must open as plain text anywhere) and the picker instead STARTS in
  // Downloads where supported (showOpenFilePicker — Chromium desktop); every
  // other browser, including the Android WebView, gets the plain file input.
  import { parseRecoveryFile, recoveryFileNamePrefix, looksLikeRecoveryFileName } from '@/services/e2ee/nc1/RecoveryCodeFile'
  import { sameAccount } from '@/services/e2ee/E2EEPasscodePrompt'

  const MAX_FILE_BYTES = 256 * 1024 // the downloaded file is ~300 bytes

  export default {
    name: 'RecoveryCodeFileButton',
    props: {
      disabled: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      },
      // The account whose identity the loaded code will unlock: names the
      // expected file and is checked against the file's own account line.
      email: {
        type: String,
        default: ''
      }
    },
    emits: ['code', 'error'],
    data () {
      return {
        mismatch: null // { code, fileEmail } awaiting "Try it anyway"
      }
    },
    computed: {
      myEmail () {
        return String(this.email || '').trim().toLowerCase()
      },
      expectedFilePrefix () {
        return recoveryFileNamePrefix(this.myEmail)
      }
    },
    watch: {
      email () {
        this.mismatch = null
      }
    },
    methods: {
      async pickFile () {
        this.mismatch = null
        if (typeof window.showOpenFilePicker === 'function') {
          let handles
          try {
            handles = await window.showOpenFilePicker({
              id: 'nc1-recovery-file',
              startIn: 'downloads',
              types: [{ description: 'Backup code file', accept: { 'text/plain': ['.txt'] } }],
              excludeAcceptAllOption: false,
              multiple: false
            })
          } catch (e) {
            if (e && e.name === 'AbortError') return // user cancelled
            // SecurityError (cross-origin iframe etc.) — fall back below.
            handles = null
          }
          if (handles && handles[0]) {
            try {
              await this.readFile(await handles[0].getFile())
            } catch (e) {
              this.$emit('error', 'Could not read that file.')
            }
            return
          }
        }
        this.$refs.fileInput.click()
      },
      async onFileInputChange (event) {
        const file = event.target.files && event.target.files[0]
        // Reset so choosing the same file again still fires `change`.
        event.target.value = ''
        if (file) await this.readFile(file)
      },
      async readFile (file) {
        if (file.size > MAX_FILE_BYTES) {
          this.$emit('error', `That file is too large to be a backup code file. Its name usually starts with ${this.expectedFilePrefix}.`)
          return
        }
        let text
        try {
          text = await file.text()
        } catch (e) {
          this.$emit('error', 'Could not read that file.')
          return
        }
        const { code, email: fileEmail } = parseRecoveryFile(text)
        if (!code) {
          this.$emit('error', looksLikeRecoveryFileName(file.name)
            ? 'No backup code was found in that file.'
            : `That doesn't look like your backup code file. Its name usually starts with ${this.expectedFilePrefix}.`)
          return
        }
        this.$emit('error', null)
        if (fileEmail && this.myEmail && !sameAccount(fileEmail, this.myEmail)) {
          this.mismatch = { code, fileEmail }
          return
        }
        this.$emit('code', code)
      },
      useAnyway () {
        if (!this.mismatch) return
        const { code } = this.mismatch
        this.mismatch = null
        this.$emit('code', code)
      }
    }
  }
</script>
